import {
  type Context,
  createContext,
  type ReactElement,
  type RefObject,
  useContext,
} from 'react';

interface ModalContainerContextValue {
  titleId: string;
  onClose: () => void;
  closeBlockedRef: RefObject<boolean>;
  fullScreen: boolean;
}

interface ModalStepsContextValue {
  pushStep: <T>(step: ReactElement) => Promise<T | undefined>;
  depth: number;
}

interface ModalStepContextValue {
  index: number;
  isTop: boolean;
  popStep?: (value?: unknown) => void;
}

export const ModalContainerContext = createContext<ModalContainerContextValue | null>(null);

export const ModalStepsContext = createContext<ModalStepsContextValue | null>(null);

export const ModalStepContext = createContext<ModalStepContextValue | null>(null);

const useRequiredContext = <T>(context: Context<T | null>, name: string): T => {
  const value = useContext(context);
  if (!value) {
    throw new Error(`${name} must be rendered inside ModalContainer`);
  }
  return value;
};

export const useModalContainerContext = () =>
  useRequiredContext(ModalContainerContext, 'ModalStep');

export const useModalStepContext = () => useRequiredContext(ModalStepContext, 'ModalStep');

export const useModalSteps = () => useRequiredContext(ModalStepsContext, 'useModalSteps');

export const useModalStep = <T = unknown>() => {
  const { index, popStep } = useRequiredContext(ModalStepContext, 'useModalStep');

  return {
    index,
    popStep: (value?: T) => popStep?.(value),
  };
};
